import { useEffect, useState, type FormEvent } from 'react';
import {
  listClientBuilds,
  createClientBuild,
  updateClientBuild,
  deleteClientBuild,
  type ClientBuild,
  type CreateClientBuildInput,
  type UpdateClientBuildInput,
} from '../../services/clientBuilds';
import type { Channel } from '../../services/releases';
import { BinaryUploader } from '../../components/BinaryUploader';
import type { UploadResult } from '../../services/binaryUpload';

// W6 corte 18.A (2026-05-11) — CRUD client_builds via painel admin.
//
// Operador gerencia builds do cliente AIKA:
//   - Catalogar build novo (name + version + hash + min_required + channel)
//   - Editar metadados (notes, hash, min_required, channel)
//   - Toggle is_active (ativa este como current no canal)
//   - Soft delete (deactivate + audit trail)
//
// Endpoint backend ja exposto desde Sprint 3. Painel agora wired pra fluxo
// completo em vez de POST direto via curl.

const channels: Channel[] = ['lts', 'stable', 'beta'];

function ChannelBadge({ channel }: { channel: Channel }) {
  const colors: Record<Channel, string> = {
    lts:    'bg-blue-900 text-blue-300 border-blue-700',
    stable: 'bg-emerald-900 text-emerald-300 border-emerald-700',
    beta:   'bg-amber-900 text-amber-300 border-amber-700',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded border ${colors[channel]}`}>
      {channel}
    </span>
  );
}

const inputCls =
  'w-full bg-[#0d0f12] border border-[#2a2f36] rounded px-3 py-1.5 text-sm text-gray-200 focus:outline-none focus:border-red-500';
const inputMonoCls = inputCls + ' font-mono';

export function ClientBuilds() {
  // ----- All hooks first (regra react_hooks_before_returns) -----
  const [items, setItems] = useState<ClientBuild[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [nameFilter, setNameFilter] = useState('');
  const [busyUuid, setBusyUuid] = useState<string | null>(null);

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [cName, setCName] = useState('AIKA');
  const [cVersion, setCVersion] = useState('');
  const [cChannel, setCChannel] = useState<Channel>('stable');
  const [cHash, setCHash] = useState('');
  const [cMinVer, setCMinVer] = useState('');
  const [cPathHint, setCPathHint] = useState('');
  const [cNotes, setCNotes] = useState('');
  const [cIsActive, setCIsActive] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Placeholder hash mode — cadastra build sem hash real do .text section.
  // Destrava o erro "build not registered for name=X" no SDK durante integração,
  // mas integrity_check vai falhar até hash real ser preenchido via edit.
  const [cPlaceholderHash, setCPlaceholderHash] = useState(false);

  // Edit form state — only one row in edit at a time
  const [editingUuid, setEditingUuid] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<UpdateClientBuildInput>({});

  // Sprint Auto-update F6 — upload binary state
  const [uploadingUuid, setUploadingUuid] = useState<string | null>(null);  // Re-upload em card
  const [createUploadResult, setCreateUploadResult] = useState<UploadResult | null>(null);  // Pre-fill Create

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listClientBuilds({ name: nameFilter || undefined, limit: 50 })
      .then((r) => {
        if (!cancelled) setItems(r.data);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || 'erro ao listar client_builds');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshTick, nameFilter]);

  async function handleCreate(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!cName.trim() || !cVersion.trim() || !cHash.trim()) {
      setError('name, version e text_hash_sha256 são obrigatórios');
      return;
    }
    if (cHash.length !== 64 || !/^[0-9a-fA-F]+$/.test(cHash)) {
      setError('text_hash_sha256 precisa ter 64 chars hex');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Defesa-em-profundidade: placeholder hash → força is_active=false no
      // backend, mesmo que checkbox tenha sido alterada antes do toggle.
      const isActiveSafe = cPlaceholderHash ? false : cIsActive;
      const input: CreateClientBuildInput = {
        name: cName.trim(),
        version: cVersion.trim(),
        channel: cChannel,
        text_hash_sha256: cHash.trim().toLowerCase(),
        exe_path_hint: cPathHint.trim() || undefined,
        notes: cNotes.trim() || undefined,
        min_required_version: cMinVer.trim() || undefined,
        is_active: isActiveSafe,
        // Sprint Auto-update F6 — se upload binary aconteceu, passa r2_object_key
        r2_object_key: createUploadResult?.r2_object_key,
      };
      await createClientBuild(input);
      // Reset form
      setCVersion('');
      setCHash('');
      setCMinVer('');
      setCPathHint('');
      setCNotes('');
      setCIsActive(false);
      setCPlaceholderHash(false);
      setCreateUploadResult(null);
      setShowCreate(false);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      setError((e as Error).message || 'erro ao criar');
    } finally {
      setSubmitting(false);
    }
  }

  // Sprint Auto-update F6 -- re-upload em card existente.
  // Apos upload OK, faz PATCH no build com r2_object_key + text_hash_sha256.
  async function handleCardUploadComplete(uuid: string, result: UploadResult): Promise<void> {
    setBusyUuid(uuid);
    setError(null);
    try {
      await updateClientBuild(uuid, {
        r2_object_key: result.r2_object_key,
        text_hash_sha256: result.text_hash_sha256,
      });
      setUploadingUuid(null);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      setError((e as Error).message || 'erro ao salvar upload');
    } finally {
      setBusyUuid(null);
    }
  }

  function startEdit(b: ClientBuild): void {
    setEditingUuid(b.id);
    setEditForm({
      channel: b.channel,
      text_hash_sha256: b.text_hash_sha256,
      exe_path_hint: b.exe_path_hint,
      notes: b.notes,
      is_active: b.is_active,
      min_required_version: b.min_required_version,
    });
  }

  function cancelEdit(): void {
    setEditingUuid(null);
    setEditForm({});
  }

  async function handleEditSave(uuid: string): Promise<void> {
    setBusyUuid(uuid);
    setError(null);
    try {
      await updateClientBuild(uuid, editForm);
      setEditingUuid(null);
      setEditForm({});
      setRefreshTick((t) => t + 1);
    } catch (e) {
      setError((e as Error).message || 'erro ao salvar');
    } finally {
      setBusyUuid(null);
    }
  }

  async function handleToggleActive(b: ClientBuild): Promise<void> {
    if (!confirm(
        `${b.is_active ? 'Desativar' : 'Ativar'} ${b.name} v${b.version} (canal ${b.channel})?`,
      )) {
      return;
    }
    setBusyUuid(b.id);
    setError(null);
    try {
      await updateClientBuild(b.id, { is_active: !b.is_active });
      setRefreshTick((t) => t + 1);
    } catch (e) {
      setError((e as Error).message || 'erro ao alterar is_active');
    } finally {
      setBusyUuid(null);
    }
  }

  async function handleDelete(b: ClientBuild): Promise<void> {
    if (!confirm(
        `Soft-delete ${b.name} v${b.version}? Row fica em DB com deleted_at preenchido (recuperável).`,
      )) {
      return;
    }
    setBusyUuid(b.id);
    setError(null);
    try {
      await deleteClientBuild(b.id);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      setError((e as Error).message || 'erro ao deletar');
    } finally {
      setBusyUuid(null);
    }
  }

  return (
    <div>
      <header className="mb-6">
        <h2 className="text-2xl font-bold text-gray-200">Client Builds</h2>
        <p className="text-sm text-gray-500 mt-1">
          W6 18.A — Catalogar / editar / ativar builds do cliente. Hash sha256 do
          .text segment é validado pelo SDK no boot via integrity_check.
        </p>
      </header>

      {/* Filter + create toggle */}
      <div className="flex items-center gap-3 mb-4">
        <input
          type="text"
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
          placeholder="Filtrar por name (ex: AIKA)"
          className={`${inputCls} flex-1 max-w-xs`}
        />
        <button
          type="button"
          onClick={() => setShowCreate((v) => !v)}
          className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded px-4 py-1.5"
        >
          {showCreate ? 'Cancelar' : '+ Novo build'}
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <form
          onSubmit={handleCreate}
          className="bg-[#161a1f] border border-[#2a2f36] rounded-lg p-4 mb-6 space-y-3"
        >
          <h3 className="text-sm font-medium text-gray-300">Criar build</h3>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Name</label>
              <input
                type="text"
                value={cName}
                onChange={(e) => setCName(e.target.value)}
                className={inputCls}
                required
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Version (semver)</label>
              <input
                type="text"
                value={cVersion}
                onChange={(e) => setCVersion(e.target.value)}
                placeholder="2.0.0"
                className={inputMonoCls}
                required
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Channel</label>
              <select
                value={cChannel}
                onChange={(e) => setCChannel(e.target.value as Channel)}
                className={inputCls}
              >
                {channels.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">text_hash_sha256 (hex 64)</label>
            <input
              type="text"
              value={cHash}
              onChange={(e) => setCHash(e.target.value)}
              placeholder="aaaaaaaa...ffff"
              className={inputMonoCls}
              required
              disabled={cPlaceholderHash}
            />
            <p className="text-xs text-gray-600 mt-1">
              Insira manual OU faça upload binary abaixo (auto-fill via SHA-256 client-side).
            </p>
          </div>

          {/* Placeholder hash toggle — destrava "build not registered" no SDK
              cadastrando antes do hash real. Build fica is_active=false. */}
          <div className="border-t border-[#2a2f36] pt-3">
            <label className="flex items-start gap-2 text-sm text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={cPlaceholderHash}
                onChange={(e) => {
                  const on = e.target.checked;
                  setCPlaceholderHash(on);
                  if (on) {
                    setCHash('0'.repeat(64));
                    setCIsActive(false);
                    setCreateUploadResult(null);
                  } else {
                    setCHash('');
                  }
                }}
                className="mt-0.5"
              />
              <div>
                <div>Cadastrar sem hash real (placeholder 00…00)</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Útil pra registrar o <code className="font-mono text-gray-400">name</code> antes
                  do binário do cliente estar pronto. Resolve <code className="font-mono text-gray-400">build not registered for name=…</code>,
                  mas o build NÃO passa <code className="font-mono text-gray-400">integrity_check</code> —
                  edite o hash real e ative depois.
                </div>
              </div>
            </label>
          </div>

          {cPlaceholderHash && (
            <div className="bg-amber-950 border border-amber-800 text-amber-300 text-xs rounded px-3 py-2">
              ⚠️ Modo placeholder ativo. Build será criado com <code className="font-mono">is_active=false</code> e
              upload binary desabilitado. SDK reportará <code className="font-mono">CLIENT_INTEGRITY_FAILED</code> até
              o hash real ser preenchido (PATCH via botão "editar" na linha).
            </div>
          )}

          {/* Sprint Auto-update F6 -- upload binary integrado no Create form */}
          {!cPlaceholderHash && cName.trim() && cVersion.trim() && !createUploadResult && (
            <div className="border-t border-[#2a2f36] pt-3">
              <div className="text-xs text-gray-500 mb-2">
                Opcional: upload binary direto pro R2. SHA-256 calculado client-side
                + presigned PUT URL TTL 15min. Pos upload o hash + r2_object_key
                sao auto-fill no formulario.
              </div>
              <BinaryUploader
                name={cName}
                version={cVersion}
                channel={cChannel}
                onComplete={(result) => {
                  setCreateUploadResult(result);
                  setCHash(result.text_hash_sha256);
                }}
                compact
              />
            </div>
          )}
          {!cPlaceholderHash && createUploadResult && (
            <div className="bg-emerald-950 border border-emerald-800 text-emerald-300 text-xs rounded px-2 py-2">
              <div>Binary uploaded! r2_object_key={createUploadResult.r2_object_key}</div>
              <div className="font-mono">sha256={createUploadResult.text_hash_sha256}</div>
              <button
                type="button"
                onClick={() => setCreateUploadResult(null)}
                className="text-xs text-gray-400 hover:text-gray-300 mt-1"
              >
                trocar binary
              </button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">min_required_version</label>
              <input
                type="text"
                value={cMinVer}
                onChange={(e) => setCMinVer(e.target.value)}
                placeholder="1.0.0"
                className={inputMonoCls}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">exe_path_hint (opcional)</label>
              <input
                type="text"
                value={cPathHint}
                onChange={(e) => setCPathHint(e.target.value)}
                placeholder="Game\\AIKA.exe"
                className={inputCls}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Notes / changelog</label>
            <textarea
              value={cNotes}
              onChange={(e) => setCNotes(e.target.value)}
              rows={3}
              className={inputCls}
            />
          </div>
          <div className="flex items-center justify-between">
            <label
              className={`flex items-center gap-2 text-sm ${
                cPlaceholderHash ? 'text-gray-600 cursor-not-allowed' : 'text-gray-400'
              }`}
            >
              <input
                type="checkbox"
                checked={cIsActive}
                onChange={(e) => setCIsActive(e.target.checked)}
                disabled={cPlaceholderHash}
              />
              Ativar imediatamente (deactivate outros do mesmo canal)
              {cPlaceholderHash && (
                <span className="text-xs italic">— bloqueado (placeholder hash)</span>
              )}
            </label>
            <button
              type="submit"
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium rounded px-4 py-1.5"
            >
              {submitting ? 'Criando...' : 'Criar build'}
            </button>
          </div>
        </form>
      )}

      {error && (
        <div className="bg-red-950 border border-red-800 text-red-300 text-sm rounded px-3 py-2 mb-4">
          {error}
        </div>
      )}

      {/* List */}
      {loading ? (
        <p className="text-gray-500">Carregando...</p>
      ) : items.length === 0 ? (
        <p className="text-gray-500">Nenhum build catalogado.</p>
      ) : (
        <div className="space-y-2">
          {items.map((b) => {
            const isEditing = editingUuid === b.id;
            return (
              <div
                key={b.id}
                className="bg-[#161a1f] border border-[#2a2f36] rounded p-3"
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <code className="text-sm text-gray-300 font-mono">
                    {b.name} v{b.version}
                  </code>
                  <ChannelBadge channel={b.channel} />
                  {b.is_active && (
                    <span className="text-xs px-2 py-0.5 rounded border bg-emerald-900 text-emerald-300 border-emerald-700">
                      ATIVO
                    </span>
                  )}
                  <span className="text-xs text-gray-500 font-mono">
                    hash={b.text_hash_sha256.slice(0, 12)}…
                  </span>
                  {b.min_required_version && (
                    <span className="text-xs text-gray-500">
                      min_req={b.min_required_version}
                    </span>
                  )}
                  <span className="text-xs text-gray-500 ml-auto">
                    {b.created_at ? new Date(b.created_at).toLocaleString() : '-'}
                  </span>
                  {!isEditing && uploadingUuid !== b.id && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => startEdit(b)}
                        disabled={busyUuid === b.id}
                        className="text-xs text-gray-400 hover:text-blue-400 transition"
                      >
                        editar
                      </button>
                      <button
                        type="button"
                        onClick={() => setUploadingUuid(b.id)}
                        disabled={busyUuid === b.id}
                        className="text-xs text-gray-400 hover:text-purple-400 transition"
                      >
                        upload binary
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleToggleActive(b)}
                        disabled={busyUuid === b.id}
                        className="text-xs text-gray-400 hover:text-emerald-400 transition"
                      >
                        {b.is_active ? 'desativar' : 'ativar'}
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDelete(b)}
                        disabled={busyUuid === b.id}
                        className="text-xs text-gray-400 hover:text-red-400 transition"
                      >
                        soft-delete
                      </button>
                    </div>
                  )}
                </div>

                {/* Edit inline form */}
                {isEditing && (
                  <div className="mt-3 pt-3 border-t border-[#2a2f36] space-y-2">
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Channel</label>
                        <select
                          value={editForm.channel || b.channel}
                          onChange={(e) =>
                            setEditForm({ ...editForm, channel: e.target.value as Channel })
                          }
                          className={inputCls}
                        >
                          {channels.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs text-gray-500 mb-1">text_hash_sha256</label>
                        <input
                          type="text"
                          value={editForm.text_hash_sha256 || ''}
                          onChange={(e) =>
                            setEditForm({ ...editForm, text_hash_sha256: e.target.value })
                          }
                          className={inputMonoCls}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">min_required_version</label>
                        <input
                          type="text"
                          value={editForm.min_required_version || ''}
                          onChange={(e) =>
                            setEditForm({ ...editForm, min_required_version: e.target.value })
                          }
                          className={inputMonoCls}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">exe_path_hint</label>
                        <input
                          type="text"
                          value={editForm.exe_path_hint || ''}
                          onChange={(e) =>
                            setEditForm({ ...editForm, exe_path_hint: e.target.value })
                          }
                          className={inputCls}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Notes / changelog</label>
                      <textarea
                        value={editForm.notes || ''}
                        onChange={(e) =>
                          setEditForm({ ...editForm, notes: e.target.value })
                        }
                        rows={3}
                        className={inputCls}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-sm text-gray-400">
                        <input
                          type="checkbox"
                          checked={editForm.is_active ?? b.is_active}
                          onChange={(e) =>
                            setEditForm({ ...editForm, is_active: e.target.checked })
                          }
                        />
                        is_active
                      </label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={cancelEdit}
                          className="text-xs text-gray-400 hover:text-gray-300 transition px-3 py-1"
                        >
                          cancelar
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleEditSave(b.id)}
                          disabled={busyUuid === b.id}
                          className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-medium rounded px-3 py-1"
                        >
                          {busyUuid === b.id ? 'Salvando...' : 'Salvar'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sprint Auto-update F6 -- re-upload binary inline (opcao A) */}
                {uploadingUuid === b.id && (
                  <div className="mt-3 pt-3 border-t border-[#2a2f36]">
                    <div className="text-xs text-gray-500 mb-2">
                      Upload binary novo pra {b.name} v{b.version}. SHA-256 +
                      r2_object_key sao atualizados via PATCH automaticamente.
                    </div>
                    <BinaryUploader
                      name={b.name}
                      version={b.version}
                      channel={b.channel}
                      onComplete={(result) => void handleCardUploadComplete(b.id, result)}
                      onCancel={() => setUploadingUuid(null)}
                      compact
                      disabled={busyUuid === b.id}
                    />
                  </div>
                )}

                {/* Notes preview when not editing */}
                {!isEditing && uploadingUuid !== b.id && b.notes && (
                  <div className="mt-2 text-xs text-gray-500 whitespace-pre-wrap">
                    {b.notes}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
